import { Button, Empty, InputNumber, Select, Table, Typography } from '@arco-design/web-react'
import { IconDelete, IconPlus } from '@arco-design/web-react/icon'
import { Pill } from '@/utils/render'
import type { CrossGroupRow, GroupRow } from '../ratioSettingUtils'
import { createRatioRowId } from '../ratioSettingUtils'

interface Props {
  rows: CrossGroupRow[]
  groupRows: GroupRow[]
  onChange: (rows: CrossGroupRow[]) => void
}

export default function CrossGroupRatioEditor({ rows, groupRows, onChange }: Props) {
  const groupKeys = new Set(groupRows.map((row) => row.key.trim()).filter(Boolean))
  const groupOptions = Array.from(groupKeys).map((key) => ({ label: key, value: key }))

  const updateRow = (id: string, patch: Partial<CrossGroupRow>) => {
    onChange(rows.map((row) => (row.id === id ? { ...row, ...patch } : row)))
  }

  const renderGroupWarning = (value: string) => {
    const key = value.trim()
    if (!key || groupKeys.has(key)) return null
    return <Pill variant="warning">未在基础分组中声明</Pill>
  }

  const columns = [
    {
      title: '用户所属分组',
      dataIndex: 'userGroup',
      render: (_: unknown, row: CrossGroupRow) => (
        <div>
          <Select
            allowCreate
            placeholder="如 vip"
            value={row.userGroup || undefined}
            options={groupOptions}
            onChange={(value) => updateRow(row.id, { userGroup: String(value ?? '') })}
          />
          {renderGroupWarning(row.userGroup)}
        </div>
      ),
    },
    {
      title: '实际使用分组',
      dataIndex: 'usingGroup',
      render: (_: unknown, row: CrossGroupRow) => (
        <div>
          <Select
            allowCreate
            placeholder="如 default"
            value={row.usingGroup || undefined}
            options={groupOptions}
            onChange={(value) => updateRow(row.id, { usingGroup: String(value ?? '') })}
          />
          {renderGroupWarning(row.usingGroup)}
        </div>
      ),
    },
    {
      title: '覆盖倍率',
      dataIndex: 'ratio',
      width: 180,
      render: (_: unknown, row: CrossGroupRow) => (
        <InputNumber
          min={0}
          step={0.1}
          className="!w-full"
          value={row.ratio}
          onChange={(value) =>
            updateRow(row.id, { ratio: value == null || !Number.isFinite(value) ? 0 : value })
          }
        />
      ),
    },
    {
      title: '操作',
      width: 90,
      render: (_: unknown, row: CrossGroupRow) => (
        <Button
          type="text"
          status="danger"
          icon={<IconDelete />}
          aria-label="删除交叉倍率"
          onClick={() => onChange(rows.filter((item) => item.id !== row.id))}
        />
      ),
    },
  ]

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <Typography.Text bold>用户组 × 使用组倍率覆盖</Typography.Text>
          <Typography.Paragraph type="secondary" className="!text-xs !mb-0">
            写入 GroupGroupRatio。左侧是用户表中的所属分组，右侧是令牌/auto/Playground 最终使用的渠道分组；
            命中后覆盖该使用分组的基础倍率，适合 VIP 折扣或专属分组策略。
          </Typography.Paragraph>
        </div>
        <Button
          type="outline"
          icon={<IconPlus />}
          onClick={() =>
            onChange([...rows, { id: createRatioRowId('cross'), userGroup: '', usingGroup: '', ratio: 1 }])
          }
        >
          添加规则
        </Button>
      </div>
      <Table
        rowKey="id"
        size="small"
        columns={columns}
        data={rows}
        pagination={false}
        noDataElement={<Empty description="暂无交叉倍率覆盖规则" />}
      />
    </div>
  )
}
